import { useQuery } from '@tanstack/react-query'
import config from '@config'

const ALLOWED_TYPES_OF_SPORT = ['soccer'] as const

export type AllowedTypeOfSport = (typeof ALLOWED_TYPES_OF_SPORT)[number]
interface UseRecentMatchOutcomesProps {
  typeOfSport: 'soccer'
  matchId: string
}

export interface RecentMatchOutcomesTeam {
  abbreviation: string
  recentMatchOutcomes: RecentMatchOutcome[]
}

export interface RecentMatchOutcomesOpponent {
  id: string
  name: string
  abbreviation: string
  image: {
    src: string
    alt: string
  }
}

export type RecentMatchOutcome = {
  matchId: string
  opponent: RecentMatchOutcomesOpponent
  type: 'win' | 'loss' | 'draw'
}

export interface RecentMatchOutcomesAPIResponse {
  homeTeam: RecentMatchOutcomesTeam
  awayTeam: RecentMatchOutcomesTeam
}

const {
  sports: { baseUrlV2: sportsBaseUrlV2, recentMatchOutcomesUrlTemplateMatchV2 },
} = config

export const validMatchId = (matchId: UseRecentMatchOutcomesProps['matchId']) =>
  !!matchId && matchId !== '0'

export const validTypeOfSport = (
  typeOfSport: UseRecentMatchOutcomesProps['typeOfSport']
) => ALLOWED_TYPES_OF_SPORT.includes(typeOfSport)

const fetchRecentMatchOutcomesData = async ({
  typeOfSport,
  matchId,
}: UseRecentMatchOutcomesProps) => {
  const requestUrl = `${sportsBaseUrlV2}${recentMatchOutcomesUrlTemplateMatchV2
    .replace('{SPORT}', typeOfSport)
    .replace('{API_VERSION}', 'v0')
    .replace('{MATCH_ID}', matchId)}`

  const response = await fetch(requestUrl)

  if (!response.ok) {
    throw new Error('Error fetching recent match outcomes.')
  }

  return response.json() as Promise<RecentMatchOutcomesAPIResponse>
}

const useRecentMatchOutcomes = ({
  matchId,
  typeOfSport,
}: UseRecentMatchOutcomesProps) => {
  const isEnabled = validMatchId(matchId) && validTypeOfSport(typeOfSport)

  return useQuery({
    queryKey: ['sportsRecentMatchOutcomes', typeOfSport, matchId],
    queryFn: () =>
      fetchRecentMatchOutcomesData({
        typeOfSport,
        matchId,
      }),
    enabled: isEnabled,
  })
}

export default useRecentMatchOutcomes
